// This page allows users to generate, edit, improve, and save questions based on selected study material.
// Integrates Firestore for data persistence, supports AI-based generation and improvement via Claude,
// and provides a rich form with RTL-aware inputs, floating buttons, toggles, and real-time feedback.

// 📦 Global styles
import "../CSS/QuestionManagement.css";

// 🧠 React & hooks
import { useState, useEffect, useMemo } from "react";

// 🌐 Routing
import { useNavigate, useLocation, useParams } from "react-router-dom";

// 🎨 MUI core components
import { Snackbar, Alert, TextField, Button, Typography, Paper, InputLabel, Select, MenuItem, FormControl, Box, Fab, Divider, ToggleButton, Tooltip } from "@mui/material";

// 🧱 MUI layout (Grid2 is from experimental layout module)
import Grid from "@mui/material/Grid2";

// 🎨 MUI theming
import { createTheme, ThemeProvider } from "@mui/material/styles";

// 🧩 MUI RTL support
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import { useQuestions } from "../../contexts/QuestionContext";

// 🎯 MUI Icons
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AddIcon from "@mui/icons-material/Add";

// 🔥 Firebase handlers
import { saveQuestion, getCategories, getMaterial, updateMaterial } from "../../firebase-handler";

import { highlightQuotesInText } from "../../utils/textHighlighter";
import { getClaudeUserErrorMessage } from "../../utils/errorMessages";

const QuestionManagementPage = () => {
  // Extract route parameters and navigation utilities from React Router
  const { categoryId, questionId } = useParams();             // Get category and question IDs from the URL
  const { state } = useLocation();                            // Access navigation state (e.g., passed data when navigating)
  const navigate = useNavigate();                             // Hook for programmatic navigation

  // Determine the initial question data based on navigation state or fallback to empty object
  const initialQuestion = state?.question || {};

  // Question Attributes
  // Initialize form state with question-related fields.
  // This state object holds all the necessary attributes for creating or editing a question.
  // If editing an existing question, use its initial values; otherwise, initialize with defaults.
  const [form, setForm] = useState({
    question: initialQuestion?.question || "",                 // The main question text
    solution: initialQuestion?.answer || "",                   // The correct answer to the question
    answers: initialQuestion?.answers || "",                   // The answers options to MOC questions
    quotation: "",                                             // Array or string of quotations supporting the question
    material: initialQuestion?.material || "",                 // The source material from which the question is derived
    difficulty: initialQuestion?.difficulty || "Medium",       // Difficulty level: Easy, Medium, or Hard
    questionType: initialQuestion?.type || "רב-ברירה",         // Type of question (e.g., multiple choice, true/false, matching)
    changeDescription: "",                                     // Describes modifications made to the question (used for audit/history)
    matchingDefinitions: "",                                   // Used in matching-type questions to store definitions
    matchingExamples: "",                                      // Used in matching-type questions to store examples
    matchingDefinitionsCount: 4,                               // Number of definition items for matching questions
    matchingExamplesCount: 4,                                  // Number of example items for matching questions
  });

  // Determine the ID of the question being edited or created
  const oldQuestionId = initialQuestion?.id || questionId;    // Prefer ID from state; fall back to route param

  // Determine the originating category (used when saving or comparing category changes)
  const oldCategory = state?.categoryId || initialQuestion.category || null;

  // State for handling optional user-provided example question (for guiding AI question generation)
  const [showExampleInput, setShowExampleInput] = useState(false);  // Controls visibility of the example question input
  const [exampleText, setExampleText] = useState("");               // Stores the user-entered example question text

  // Constant Values
  const [questionTypes] = useState(["רב-ברירה", "נכון/לא נכון", "התאמה"]); // Define the question types

  // Import from QuestionContext to handle question generation.
  const { handleQuestionGeneration, handleRegenerate } = useQuestions();

  // State for user-provided feedback and improvement toggles
  const [improvementNotes, setImprovementNotes] = useState("");       // Free-text notes used to guide AI in improving the question
  const [selectedToggles, setSelectedToggles] = useState([]);         // Preset checklist items selected by the user for structured feedback
  const builtInSuggestions = [
    {
      id: "uniform-style",
      label: "שמור על ניסוח דומה בין התשובות כדי לא למשוך תשומת לב לתשובה הנכונה",
    },
    {
      id: "valid-assessment",
      label: "וודא שהשאלה בוחנת את הידע הרצוי",
    },
    {
      id: "consistent-length",
      label: "שמור על אורך ומבנה אחיד בין כל התשובות",
    },
    {
      id: "neutral-wording",
      label: "שמור על ניסוח שאלה ניטרלי שלא יכוון את הנבחן לתשובה מסוימת",
    },
    {
      id: "plausible-distractors",
      label: "ודא שלכל תשובה שגויה יש אלמנט שמקשה על הזיהוי שהיא לא נכונה",
    },
  ];

  // UI flags and guidance inputs
  const [showSuggestions, setShowSuggestions] = useState(false);      // Controls visibility of generated suggestions or improvements
  const [specifics, setSpecifics] = useState(initialQuestion?.specifics || ""); // Additional guidelines provided by the user (e.g., pedagogy tips)
  const [category, setCategory] = useState(categoryId || "");         // Currently selected category/unit of the question

  // Snackbar (toast) feedback mechanism
  const [snackbarOpen, setSnackbarOpen] = useState(false);            // Controls the open/close state of the Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");         // Message to display in the Snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Severity level: 'success', 'error', 'warning', or 'info'

  // Material editing state
  const [isEditingMaterial, setIsEditingMaterial] = useState(false);  // Indicates whether the material section is currently being edited
  const [originalMaterial, setOriginalMaterial] = useState("");       // Stores the last saved material text for comparison with edits

  // State for storing the list of available categories fetched from the database.
  // These categories are typically used to associate a question with a specific learning unit.
  const [categories, setCategories] = useState([]);

  // State for holding custom category labels (used specifically in matching-type questions).
  // This allows users to define their own category names when creating matching exercises.
  const [customCategoryNames, setCustomCategoryNames] = useState([]);

  // Create a custom MUI theme with right-to-left (RTL) layout direction.
  // This ensures that all MUI components follow RTL alignment for Hebrew or Arabic content.
  const theme = createTheme({ direction: "rtl" });

  // Set up Emotion's caching mechanism with RTL support.
  // The stylis plugins (`prefixer` and `rtlPlugin`) transform styles to support RTL rendering.
  const cacheRtl = createCache({
    key: "muirtl",                        // Unique key for identifying the RTL cache
    stylisPlugins: [prefixer, rtlPlugin] // Enables automatic style prefixing and RTL transformation
  });

  // Utility function to detect if a string contains Hebrew characters.
  // Used to determine text direction (RTL vs. LTR) dynamically based on user input.
  const isHebrew = (text) => /[\u0590-\u05FF]/.test(text);

  // Dynamically determine the text direction for various input fields
  // based on whether the content is in Hebrew (RTL) or not (LTR).

  const isMaterialHebrew = useMemo(
    () => (isHebrew(form.material) ? "rtl" : "ltr"),
    [form.material]
  );
  
  const isSpecificsHebrew = useMemo(() => isHebrew(specifics), [specifics]);

  // Utility function to update a single field in the form state
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Utility function to update multiple fields in the form state at once
  const updateFields = (fields) => {
    setForm((prev) => ({ ...prev, ...fields }));
  };

  // Memoized transformation of the material text with quotation highlights
  // Recomputes only when either the material or quotation changes
  const highlightedMaterial = useMemo(() => {
    return highlightQuotesInText(form.material, form.quotation);
  }, [form.material, form.quotation]);

  // Syncs the number of custom category name fields based on the matching question type count
  // This is only relevant for 'התאמה' (matching) question types
  useEffect(() => {
    if (form.questionType === "התאמה") {
      const count = form.matchingDefinitionsCount || 0;
      setCustomCategoryNames((prev) => {
        const resized = [...prev];
        while (resized.length < count) resized.push(""); // Add empty slots if needed
        return resized.slice(0, count); // Trim to match the required count
      });
    }
  }, [form.matchingDefinitionsCount, form.questionType]);

  // Handles AI-based generation of a new question based on the current form input.
  // Prepares input parameters, sends them to the generation handler, and updates the form state accordingly.
  const generateQuestionFromInput = async () => {
    // 🧹 Reset any previously saved description of manual changes before generation
    updateField("changeDescription", ""); // Reset any prior change description
    try {
      // 🧠 Call the question generation function with current form input and context
      await handleQuestionGeneration(
        {
          // Core inputs used by the AI to generate the question
          materialText: form.material,                // Source learning content
          highlightsNGuidelines: specifics,           // Optional notes or focus points for generation
          complexity: form.difficulty,                // Difficulty level (e.g., Easy, Medium, Hard)
          questionType: form.questionType,            // Type of question to generate (e.g., multiple choice)
          
          // Used specifically for "matching" question types
          matchingDefinitionsCount: form.matchingDefinitionsCount,
          matchingExamplesCount: form.matchingExamplesCount,
          customCategoryNames: customCategoryNames.filter((n) => n.trim() !== ""),  // Remove any empty or invalid custom categories

          // Optional example input from the user to guide AI generation
          userExample: exampleText.trim(),
          showExampleInput: showExampleInput,
          exampleText: exampleText,
        },
        updateFields  // Callback to update the form state with the AI-generated result
      );
      // ✅ If generation succeeds, show AI suggestions in the UI
      setShowSuggestions(true);
    } catch (error) {
      // ❌ Catch and log any generation error
      console.error("❌ Claude error:", error);
      // Display user-friendly error message via snackbar
      setSnackbarMessage(getClaudeUserErrorMessage(error));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handles refining an existing question using user-provided feedback.
  // This function validates input, sends the data to an AI handler, and provides user feedback.
  const refineQuestionWithFeedback = async () => {
    // ✅ Validation: ensure all required fields are filled
    // - A question and solution must be provided
    // - At least one type of feedback (free text or predefined toggle) must be included
    if (
      !form.question.trim() ||
      !form.solution.trim() ||
      (!improvementNotes.trim() && selectedToggles.length === 0)
    ) {
      // If validation fails, notify the user and abort the operation
      setSnackbarMessage("יש למלא את השאלה, התשובה וההערות לשיפור");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    // 🧾 Prepare the form data to be sent to the AI service
    // Includes question metadata such as difficulty, type, and related learning material
    const formData = {
      question: form.question,
      solution: form.solution,
      answers: form.answers,
      materialText: form.material,
      highlightsNGuidelines: specifics,
      complexity: form.difficulty,
      questionType: form.questionType,
      matchingDefinitionsCount: form.matchingDefinitionsCount,
      matchingExamplesCount: form.matchingExamplesCount,
      showExampleInput: showExampleInput,
      exampleText: exampleText,
    };
    
    try {
      // 🧠 Trigger the AI-powered regeneration logic
      // Pass in the form data, free-text feedback, toggle selections, and the update callback
      await handleRegenerate(
        formData,
        improvementNotes,
        selectedToggles,
        updateFields
      );

      // 🎉 Notify user of success
      setSnackbarMessage("השאלה שופרה בהצלחה!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // 🧹 Clear feedback input fields only after successful improvement
      setImprovementNotes("");
      setSelectedToggles([]);
    } catch (error) {
      // ❌ Handle errors gracefully
      // Log for debugging and notify the user with a friendly message
      console.error("❌ Error improving question:", error);
      setSnackbarMessage(getClaudeUserErrorMessage(error));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handles user cancellation of the question creation/editing process.
  // Displays a cancellation message and redirects the user back to the category page after a short delay.
  const handleCancel = () => {
    // 🔔 Notify the user that the question addition/editing was cancelled
    setSnackbarMessage("הוספת השאלה בוטלה");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    // ⏳ After a brief delay, navigate back to the relevant category page
    // This gives the user time to see the snackbar message before redirecting
    setTimeout(() => navigate(`/categories/${category}`), 1000);
  };

  // Closes the snackbar notification.
  // Typically triggered by user interaction or after the auto-hide timeout.
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Toggles between viewing and editing mode for the learning material.
  // If editing is active and changes were made, it attempts to persist the updated material to Firestore.
  const handleToggleEditMaterial = async () => {
    // ✅ If user is exiting edit mode and material was modified, update it in the backend
    if (isEditingMaterial && form.material !== originalMaterial) {
      try {
        // 🔄 Persist updated material to Firestore under the current category
        await updateMaterial(category, form.material);
        
        // 🧭 Update the originalMaterial reference to reflect the new saved version
        setOriginalMaterial(form.material);
      } catch (err) {
        // ❌ Handle potential update failure (e.g., network or permission error)
        console.error("❌ Failed to update material:", err);
      }
    }

    // 🔁 Toggle between edit and view mode
    setIsEditingMaterial(!isEditingMaterial);
  };

  // 📦 useEffect to fetch learning material when the selected category changes.
  // This ensures that the form is populated with the relevant material for the selected category.
  useEffect(() => {
    // Define an async function to retrieve the material from the backend (e.g., Firestore)
    async function fetchMaterial() {
      if (category) {
        try {
          // ✅ Attempt to fetch material text for the current category
          const materialText = await getMaterial(category);

          // 📝 Update both the form state and the original reference for comparison/editing
          updateField("material", materialText);
          setOriginalMaterial(materialText);
        } catch (error) {
          // ❌ If fetching fails, fall back to a default message and store it
          updateField("material", "לא נמצא חומר לימוד");
          setOriginalMaterial("לא נמצא חומר לימוד");
        }
      }
    }

    // Invoke the fetch function
    fetchMaterial();
  }, [category]); // 🔁 Re-run only when the category changes

  useEffect(() => {
    async function fetchCategories() {
      try {
        // Fetch categories from Firestore
        const categories = await getCategories();
        setCategories(categories);
        // console.log("📂 Fetched Categories from Firestore:", categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    }

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles saving the question to the database (e.g., Firestore).
  // Validates input, constructs the question object, persists it, and provides user feedback.
  const handleSave = async () => {
    try {
      // ❗ Ensure a category is selected before saving
      if (!category) {
        console.error("❌ Missing category");
        return;
      }

      // 📝 Construct the question payload from the form state
      const questionData = {
        question: form.question,             // The question text
        answer: form.solution,              // The correct answer or solution
        difficulty: form.difficulty,        // Question difficulty level
        specifics,                          // Optional guidance or focus areas
        type: form.questionType,            // Question type (e.g., multiple choice)
        createdAt: new Date().toISOString() // Timestamp for audit or sorting
      };

      // 🛠️ Persist the question to Firestore and get the generated ID (if new)
      const savedQuestionId = await saveQuestion(
        category,         // Target category to save under
        questionId,       // Existing question ID (for edit), or undefined (for new)
        questionData,     // Structured question payload
        oldCategory,      // If the question was moved from another category
        oldQuestionId     // Original ID if this is an update
      );

      // ✅ Log and notify the user of success
      // console.log("✅ Question saved successfully with ID:", savedQuestionId);
      setSnackbarMessage("השאלה נשמרה בהצלחה!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // ⏳ Delay navigation slightly to allow user to read the message
      setTimeout(() => navigate(`/categories/${category}`), 1000);
    } catch (error) {
      // ❌ Log any unexpected failure during the save operation
      console.error("❌ Error saving question:", error);
    }
  };

  return (
    // Provide RTL-aware Emotion cache for styling
    <CacheProvider value={cacheRtl}>
       {/* Apply RTL direction to the MUI theme */}
      <ThemeProvider theme={theme}>
        {/* Main container box with margin and padding */}
        <Box sx={{ mt: 15, mb: 5, px: 4, width: "100%" }}>
          {/* Responsive grid layout with two main panels side-by-side */}
          <Grid
            container
            spacing={4}
            sx={{
              width: "100%",
              flexWrap: "nowrap", // 👉 prevents wrapping on one line
              alignItems: "stretch", // 👉 keeps height consistent
            }}
          >
            {/* Left panel: Material editor */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexBasis: 0,
                flexGrow: 1,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  pl: 6,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  position: "relative",
                }}
              >

                {/* Title */}
                <Typography variant="h5" gutterBottom>
                  חומר לימוד
                </Typography>

                {/* Material content box (editable or preview) */}
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                    height: "500px",
                    mb: 2,
                  }}
                >
                  {/* Toggle between editable TextField and preview */}
                  {isEditingMaterial ? (
                    <TextField
                      multiline
                      fullWidth
                      variant="outlined"
                      value={form.material}
                      onChange={(e) => updateField("material", e.target.value)}
                      sx={{
                        flexGrow: 1,
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                        display: "flex",
                      }}
                      InputProps={{
                        sx: {
                          alignItems: "flex-start",
                        },
                        inputProps: {
                          style: {
                            direction: isMaterialHebrew ? "ltr" : "rtl",
                            textAlign: isMaterialHebrew ? "left" : "right",
                          },
                        },
                      }}
                    />
                  ) : (
                    // Preview mode with highlighting
                    <Box
                      sx={{
                        flexGrow: 1,
                        overflow: "auto",
                        direction: isMaterialHebrew ? "rtl" : "ltr",
                        textAlign: isMaterialHebrew ? "right" : "left",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "1rem",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightedMaterial,
                      }}
                    />
                  )}
                </Box>

                {/* Floating Action Button to toggle edit/save mode */}
                <Fab
                  color="primary"
                  size="small"
                  onClick={handleToggleEditMaterial}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    zIndex: 10,
                  }}
                >
                  {isEditingMaterial ? <SaveIcon /> : <EditIcon />}
                </Fab>
              </Paper>
            </Grid>

            {/* Right panel: Question parameter form */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexBasis: 0,
                flexGrow: 1,
                pr: 7,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  pl: 6,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  position: "relative",
                }}
              >

                {/* Section title */}
                <Typography variant="h5" gutterBottom>
                  פרמטרים לשאלה
                </Typography>

                {/* Category selector */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>יחידת לימוד</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="יחידת לימוד"
                  >
                    <MenuItem value="" disabled>
                      בחר יחידת לימוד
                    </MenuItem>
                    {categories.map((cat) => {
                      return (
                        <MenuItem
                          key={cat.id}
                          value={cat.id}
                          sx={{
                            direction: isHebrew(cat.name) ? "rtl" : "ltr",
                            textAlign: isHebrew(cat.name) ? "right" : "left",
                          }}
                        >
                          {cat.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {/* Question type selector */}
                {!showExampleInput &&
                <FormControl fullWidth margin="normal">
                  <InputLabel>סוג שאלה</InputLabel>
                  <Select
                    value={form.questionType}
                    onChange={(e) =>
                      updateField("questionType", e.target.value)
                    }
                    label="סוג שאלה"
                  >
                    <MenuItem value="" disabled>
                      בחר סוג שאלה
                    </MenuItem>
                    {questionTypes.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}

                {/* Difficulty selector */}
                {!showExampleInput &&
                <FormControl fullWidth margin="normal">
                  <InputLabel>רמת קושי</InputLabel>
                  <Select
                    value={form.difficulty}
                    onChange={(e) => updateField("difficulty", e.target.value)}
                    label="רמת קושי"
                  >
                    <MenuItem value="Easy">קל</MenuItem>
                    <MenuItem value="Medium">בינוני</MenuItem>
                    <MenuItem value="Hard">קשה</MenuItem>
                  </Select>
                </FormControl>}

                {/* Toggle for example input */}
                <Tooltip
                  title={
                    showExampleInput
                      ? "הסר את הדוגמה שכתבת"
                      : "הוסף דוגמה משלך לשאלה כדי לכוון טוב יותר את המערכת (Few-Shot)"
                  }
                >
                  <Button
                    startIcon={showExampleInput ? <CloseIcon /> : <AddIcon />}
                    variant={showExampleInput ? "contained" : "outlined"}
                    color={showExampleInput ? "error" : "primary"}
                    sx={{ mt: 1, mb: 1 }}
                    onClick={() => setShowExampleInput((prev) => !prev)}
                  >
                    {showExampleInput ? "הסר דוגמה לשאלה" : "הוסף דוגמה לשאלה"}
                  </Button>
                </Tooltip>

                {/* User-provided example */}
                {showExampleInput && (
                  <TextField
                    label="שאלה לדוגמא"
                    multiline
                    fullWidth
                    rows={4}
                    value={exampleText}
                    onChange={(e) => setExampleText(e.target.value)}
                    margin="normal"
                    InputProps={{
                      sx: { "& textarea": { resize: "vertical" } },
                      inputProps: {
                        style: {
                          direction: isHebrew(exampleText) ? "rtl" : "ltr",
                          textAlign: isHebrew(exampleText) ? "right" : "left",
                        },
                      },
                    }}
                  />
                )}

                {/* Matching question configuration */}
                {!showExampleInput && form.questionType === "התאמה" && (
                  <>
                    <TextField
                      type="number"
                      label="מספר קטגוריות"
                      fullWidth
                      margin="normal"
                      value={form.matchingDefinitionsCount}
                      inputProps={{ min: 2 }}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        updateFields({
                          matchingDefinitionsCount: value,
                          matchingExamplesCount: Math.max(
                            form.matchingExamplesCount,
                            value
                          ), // Sync examples count
                        });
                      }}
                    />
                    <TextField
                      type="number"
                      label="מספר משפטים"
                      fullWidth
                      margin="normal"
                      value={form.matchingExamplesCount}
                      inputProps={{ min: 1 }}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        updateField(
                          "matchingExamplesCount",
                          Math.max(form.matchingDefinitionsCount, value)
                        );
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                      שמות הקטגוריות
                    </Typography>
                    {customCategoryNames.map((name, index) => (
                      <TextField
                        key={index}
                        fullWidth
                        margin="dense"
                        label={`קטגוריה ${index + 1}`}
                        value={name}
                        onChange={(e) => {
                          const updated = [...customCategoryNames];
                          updated[index] = e.target.value;
                          setCustomCategoryNames(updated);
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Instructional notes */}
                <TextField
                  label="הנחיות והערות"
                  multiline
                  fullWidth
                  rows={4}
                  variant="outlined"
                  value={specifics}
                  onChange={(e) => setSpecifics(e.target.value)}
                  margin="normal"
                  InputProps={{
                    sx: { "& textarea": { resize: "vertical" } },
                    inputProps: {
                      style: {
                        direction: isSpecificsHebrew ? "rtl" : "ltr",
                        textAlign: isSpecificsHebrew ? "right" : "left",
                      },
                    },
                  }}
                />

                {/* Trigger AI question generation */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={generateQuestionFromInput}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  צור!
                </Button>
              </Paper>
            </Grid>
          </Grid>

          {/* Divider for clarity */}
          <Divider sx={{ my: 4 }}>
            <Typography variant="subtitle1" color="text.secondary">
              ניסוח השאלה
            </Typography>
          </Divider>

          {/* Main question editor, improvements, suggestions, and action buttons */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pr: 7,
            }}
          >
             {/* Question Display */}
            <TextField
              label="שאלה"
              multiline
              fullWidth
              minRows={4}
              maxRows={9}
              variant="outlined"
              value={form.question}
              onChange={(e) => updateField("question", e.target.value)}
              InputProps={{
                inputProps: {
                  style: {
                    direction: isHebrew(form.question) ? "rtl" : "ltr",
                    textAlign: isHebrew(form.question) ? "right" : "left",
                  },
                },
                sx: {
                  "& textarea": {
                    resize: "vertical",
                    overflow: "auto",
                  },
                },
              }}
            />

            {/* Change description summary after regenerate improved question */}
            {form.changeDescription && (
              <Paper
                sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  שינויים שבוצעו בשאלה:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  <div dir="rtl" style={{ textAlign: "right" }}>
                    {form.changeDescription}
                  </div>
                </Typography>
              </Paper>
            )}

            {/* Display Answer */}
            <TextField
              label="תשובה"
              multiline
              fullWidth
              minRows={4}
              maxRows={9}
              variant="outlined"
              value={form.solution}
              onChange={(e) => updateField("solution", e.target.value)}
              InputProps={{
                inputProps: {
                  style: {
                    direction: isHebrew(form.solution) ? "rtl" : "ltr",
                    textAlign: isHebrew(form.solution) ? "right" : "left",
                  },
                },
                sx: {
                  "& textarea": {
                    resize: "vertical",
                    overflow: "auto",
                  },
                },
              }}
            />

            {/* Display AI-quoted material (if available) */}
            {Array.isArray(form.quotation) && form.quotation.length > 0 && (
              <TextField
                label="ציטוטים מהחומר"
                multiline
                fullWidth
                minRows={4}
                maxRows={12}
                variant="outlined"
                value={form.quotation.map((q) => `"${q.trim()}"`).join("\n\n")}
                InputProps={{
                  readOnly: true,
                  inputProps: {
                    style: {
                      direction: isHebrew(form.quotation) ? "rtl" : "ltr",
                      textAlign: isHebrew(form.quotation) ? "right" : "left",
                    },
                  },
                  sx: {
                    "& textarea": {
                      resize: "vertical",
                      overflow: "auto",
                    },
                  },
                }}
              />
            )}

            {/* Feedback notes for AI improvement */}
            <TextField
              label="הערות לשיפור"
              multiline
              fullWidth
              minRows={4}
              maxRows={9}
              variant="outlined"
              value={improvementNotes}
              onChange={(e) => setImprovementNotes(e.target.value)}
              InputProps={{
                sx: { "& textarea": { resize: "vertical", overflow: "auto" } },
                inputProps: {
                  style: {
                      direction: isHebrew(improvementNotes) ? "rtl" : "ltr",
                      textAlign: isHebrew(improvementNotes) ? "right" : "left",
                  },
                },
              }}
            />

            {/* Suggestion toggle buttons for AI hints */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "stretch",
                mt: 2,
                gap: 1,
              }}
            >
              {builtInSuggestions.map(({ id, label }) => (
                <ToggleButton
                  key={id}
                  value={id}
                  selected={selectedToggles.includes(id)}
                  onChange={() => {
                    setSelectedToggles((prev) =>
                      prev.includes(id)
                        ? prev.filter((item) => item !== id)
                        : [...prev, id]
                    );
                  }}
                  sx={{
                    flex: 1,
                    maxWidth: "19%",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: "bold",
                    textAlign: "center",
                    border: "1px solid rgba(0,0,0,0.12)",
                    py: 3,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  }}
                >
                  {label}
                </ToggleButton>
              ))}
            </Box>

            {/* Action buttons for Cancel, Improve, Save */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Tooltip title="ביטול השינויים וחזרה לעמוד הקודם">
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                  fullWidth
                  startIcon={<CloseIcon />}
                >
                  בטל
                </Button>
              </Tooltip>
              <Tooltip title="שיפור השאלה על סמך ההערות שכתבת">
                <Button
                  variant="contained"
                  color="info"
                  onClick={refineQuestionWithFeedback}
                  fullWidth
                  startIcon={<AutoFixHighIcon />}
                >
                  שפר
                </Button>
              </Tooltip>

              <Tooltip title="שמירת השאלה במאגר">
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSave}
                  fullWidth
                  startIcon={<SaveIcon />}
                >
                  שמור
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Snackbar for user feedback messages */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={5000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbarSeverity}
              onClose={handleCloseSnackbar}
              sx={{
                width: "100%",
                fontSize: "1.1rem",
                py: 2,
                px: 3,
              }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default QuestionManagementPage;
